import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import Modal from "@/components/base/Modal";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useBiens } from "@/pages/proprietaire/biens/BiensProvider";
import { BIEN_TYPE_LABELS } from "@/pages/proprietaire/biens/types";
import { useContrats } from "@/pages/proprietaire/contrats/ContratsProvider";
import { contratReference } from "@/pages/proprietaire/contrats/types";
import { useImmeubles } from "@/pages/proprietaire/immeubles/ImmeublesProvider";
import { useLocataires } from "@/pages/proprietaire/locataires/LocatairesProvider";
import { locataireNomComplet } from "@/pages/proprietaire/locataires/types";
import type { ExportColumn } from "@/utils/export";
import { formatDate } from "@/utils/format";
import DocumentForm from "./components/DocumentForm";
import DocumentPreviewModal from "./components/DocumentPreviewModal";
import { useDocuments } from "./DocumentsProvider";
import {
  DOCUMENT_TYPE_ICONS,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPE_OPTIONS,
  DOCUMENT_TYPE_TONES,
  formatFileSize,
  type DocumentItem,
} from "./types";

type SortKey = "recent" | "ancien" | "nom";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "ancien", label: "Plus anciens" },
  { value: "nom", label: "Nom (A → Z)" },
];

export default function DocumentsPage() {
  const { documents, stats, countByType, removeDocument } = useDocuments();
  const { immeubles } = useImmeubles();
  const { biens, getBien } = useBiens();
  const { contrats } = useContrats();
  const { locataires } = useLocataires();
  const [searchParams, setSearchParams] = useSearchParams();

  const immeubleParam = searchParams.get("immeuble") ?? "";
  const bienParam = searchParams.get("bien") ?? "";
  const nouveauParam = searchParams.get("nouveau") === "1";

  const [formOpen, setFormOpen] = useState(nouveauParam);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [immeubleFilter, setImmeubleFilter] = useState(immeubleParam);
  const [bienFilter, setBienFilter] = useState(bienParam);
  const [sort, setSort] = useState<SortKey>("recent");
  const [preview, setPreview] = useState<DocumentItem | null>(null);
  const [toDelete, setToDelete] = useState<DocumentItem | null>(null);
  const [editing, setEditing] = useState<DocumentItem | null>(null);

  useEffect(() => {
    if (nouveauParam) setFormOpen(true);
  }, [nouveauParam]);

  const hasDocuments = documents.length > 0;

  const immeubleOptions = useMemo(
    () => [{ value: "", label: "Tous les immeubles" }, ...immeubles.map((i) => ({ value: i.id, label: i.nom }))],
    [immeubles]
  );

  const bienOptions = useMemo(
    () => [
      { value: "", label: "Tous les biens" },
      ...biens
        .filter((bien) => !immeubleFilter || bien.immeubleId === immeubleFilter)
        .map((bien) => ({
          value: bien.id,
          label: `${BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}`,
        })),
    ],
    [biens, immeubleFilter]
  );

  const typeOptions = useMemo(
    () => [{ value: "", label: "Tous les types" }, ...DOCUMENT_TYPE_OPTIONS],
    []
  );

  const closeForm = () => {
    setFormOpen(false);
    if (nouveauParam) {
      const next = new URLSearchParams(searchParams);
      next.delete("nouveau");
      setSearchParams(next, { replace: true });
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = documents.filter((document) => {
      const matchesType = !typeFilter || document.type === typeFilter;
      const matchesImmeuble = !immeubleFilter || document.immeubleId === immeubleFilter;
      const matchesBien = !bienFilter || document.bienId === bienFilter;

      const bien = document.bienId ? getBien(document.bienId) : undefined;
      const immeuble = immeubles.find((item) => item.id === document.immeubleId);
      const matchesSearch =
        !term ||
        [
          document.nom,
          document.fichierNom,
          DOCUMENT_TYPE_LABELS[document.type],
          bien ? BIEN_TYPE_LABELS[bien.type] : "",
          bien?.reference ?? "",
          immeuble?.nom ?? "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(term);

      return matchesType && matchesImmeuble && matchesBien && matchesSearch;
    });

    return result.sort((a, b) => {
      if (sort === "nom") return a.nom.localeCompare(b.nom, "fr");
      if (sort === "ancien") return new Date(a.date).getTime() - new Date(b.date).getTime();
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [documents, search, typeFilter, immeubleFilter, bienFilter, sort, getBien, immeubles]);

  const isFiltering =
    Boolean(search.trim()) || Boolean(typeFilter) || Boolean(immeubleFilter) || Boolean(bienFilter);

  const resetFilters = () => {
    setSearch("");
    setTypeFilter("");
    setImmeubleFilter("");
    setBienFilter("");
  };

  const exportColumns: ExportColumn<DocumentItem>[] = [
    { header: "Nom", value: (document) => document.nom },
    { header: "Type", value: (document) => DOCUMENT_TYPE_LABELS[document.type] },
    { header: "Date", value: (document) => formatDate(document.date) },
    { header: "Immeuble", value: (document) => immeubles.find((item) => item.id === document.immeubleId)?.nom ?? "" },
    {
      header: "Bien",
      value: (document) => {
        const bien = document.bienId ? getBien(document.bienId) : undefined;
        return bien ? `${BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` ${bien.reference}` : ""}` : "";
      },
    },
    { header: "Fichier", value: (document) => document.fichierNom },
    { header: "Taille", value: (document) => formatFileSize(document.fichierTaille) },
  ];

  const columns: DataTableColumn<DocumentItem>[] = [
    {
      key: "nom",
      header: "Document",
      className: "min-w-[240px]",
      render: (document) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-accent-700">
            <i className={`${DOCUMENT_TYPE_ICONS[document.type]} text-lg`} aria-hidden="true" />
          </span>
          <span className="truncate font-label font-medium text-foreground-950">{document.nom}</span>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (document) => (
        <Badge tone={DOCUMENT_TYPE_TONES[document.type]}>{DOCUMENT_TYPE_LABELS[document.type]}</Badge>
      ),
    },
    {
      key: "rattachement",
      header: "Rattachement",
      className: "min-w-[180px]",
      render: (document) => {
        const bien = document.bienId ? getBien(document.bienId) : undefined;
        const immeuble = immeubles.find((item) => item.id === document.immeubleId);
        const contrat = document.contratId ? contrats.find((item) => item.id === document.contratId) : undefined;
        const locataire = document.locataireId
          ? locataires.find((item) => item.id === document.locataireId)
          : undefined;
        const rattachement = [
          contrat ? contratReference(contrat.id) : "",
          locataire ? locataireNomComplet(locataire) : "",
          bien ? BIEN_TYPE_LABELS[bien.type] : "",
          immeuble?.nom ?? "",
        ]
          .filter(Boolean)
          .join(" · ");
        return <span className="text-foreground-700">{rattachement || "Aucun"}</span>;
      },
    },
    {
      key: "date",
      header: "Date",
      className: "min-w-[120px]",
      render: (document) => <span className="text-foreground-700">{formatDate(document.date)}</span>,
    },
    {
      key: "taille",
      header: "Taille",
      className: "min-w-[100px]",
      render: (document) => <span className="text-foreground-700">{formatFileSize(document.fichierTaille)}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[220px]",
      render: (document) => (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setPreview(document)}
            aria-label={`Aperçu de ${document.nom}`}
            className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-eye-line text-base" aria-hidden="true" />
          </button>
          <a
            href={document.fichierUrl || undefined}
            download={document.fichierNom || document.nom}
            aria-label={`Télécharger ${document.nom}`}
            className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-download-2-line text-base" aria-hidden="true" />
          </a>
          <button
            type="button"
            onClick={() => setEditing(document)}
            aria-label={`Modifier ${document.nom}`}
            className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3 py-2 text-xs font-label font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-edit-line text-base" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => setToDelete(document)}
            aria-label={`Supprimer ${document.nom}`}
            className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3 py-2 text-xs font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
          >
            <i className="ri-delete-bin-line text-base" aria-hidden="true" />
          </button>
        </div>
      ),
    },
  ];

  const confirmDelete = () => {
    if (toDelete) removeDocument(toDelete.id);
    setToDelete(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Documents</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Votre bibliothèque de documents : contrats, pièces d'identité, reçus, documents des biens et factures, avec
            aperçu, téléchargement et suppression.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu rows={filtered} columns={exportColumns} filename="documents" title="Documents" />
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-add-line text-base" aria-hidden="true" />
            Ajouter un document
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-folder-line" label="Documents" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-file-text-line" label="Contrats" value={String(countByType("contrat"))} tone="accent" />
        <StatCard icon="ri-receipt-line" label="Reçus & factures" value={String(countByType("recu") + countByType("facture"))} tone="secondary" />
        <StatCard icon="ri-id-card-line" label="Pièces d'identité" value={String(countByType("piece-identite"))} tone="neutral" />
      </div>

      {hasDocuments && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <label htmlFor="doc-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id="doc-search"
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Nom, fichier, immeuble, bien..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="doc-type-filter" className="text-xs font-label font-medium text-foreground-700">
                Type
              </label>
              <Select
                id="doc-type-filter"
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value)}
                options={typeOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="doc-immeuble-filter" className="text-xs font-label font-medium text-foreground-700">
                Immeuble
              </label>
              <Select
                id="doc-immeuble-filter"
                value={immeubleFilter}
                onChange={(event) => {
                  setImmeubleFilter(event.target.value);
                  setBienFilter("");
                }}
                options={immeubleOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <label htmlFor="doc-bien-filter" className="text-xs font-label font-medium text-foreground-700">
                Bien
              </label>
              <Select
                id="doc-bien-filter"
                value={bienFilter}
                onChange={(event) => setBienFilter(event.target.value)}
                options={bienOptions}
                disabled={immeubleFilter !== "" && bienOptions.length <= 1}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 border-t border-background-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-foreground-500">
              {filtered.length} document{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {documents.length}
            </p>
            <div className="flex items-center gap-3">
              {isFiltering && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm" aria-hidden="true" />
                  Réinitialiser
                </button>
              )}
              <div className="w-44">
                <Select
                  aria-label="Trier par"
                  value={sort}
                  onChange={(event) => setSort(event.target.value as SortKey)}
                  options={sortOptions}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {!hasDocuments && (
        <EmptyState
          icon="ri-folder-line"
          title="Aucun document"
          description="Ajoutez votre premier document pour centraliser contrats, pièces d'identité, reçus et documents de vos biens."
          action={
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter un document
            </button>
          }
        />
      )}

      {hasDocuments && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun document ne correspond à votre recherche ou aux filtres sélectionnés. Essayez de modifier vos critères."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(document) => document.id}
          minWidthClass="min-w-[1180px]"
          caption="Documents"
        />
      )}

      <DocumentForm
        open={formOpen || Boolean(editing)}
        initial={editing ?? undefined}
        onClose={() => {
          closeForm();
          setEditing(null);
        }}
        defaultImmeubleId={immeubleParam || undefined}
        defaultBienId={bienParam || undefined}
      />

      <DocumentPreviewModal document={preview} onClose={() => setPreview(null)} />

      <Modal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        size="sm"
        title="Supprimer le document"
        description={
          toDelete
            ? `Voulez-vous vraiment supprimer « ${toDelete.nom} » ? Cette action est définitive.`
            : undefined
        }
      >
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => setToDelete(null)}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={confirmDelete}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-delete-bin-line text-base" aria-hidden="true" />
            Supprimer
          </button>
        </div>
      </Modal>
    </div>
  );
}